import {} from '../components/safeFrame.js';
/**
 * @desc Contains safeframe client APIs that are common to desktop and mobile safeframe.
 */
import { producePlayingTimeInfo, produceViewableInfo, update, updatePlayingTime } from '../components/sfAPI.js';
import { LOADED_HOST_SCOPE, LatencyMetricType } from '../components/metrics/latency-metric-type.js';
import type { ExtendedUpdateViewabilityData, InternalSFClientAPI, LogInvocation } from './InternalSFClientAPI.js';
import type { SFClientAPI, SFClientVideoAPI } from '@amzn/safe-frame-client-amzn';
import { isVideo } from '../components/util.js';
import { ClientMessageSender } from './clientMessageSender.js';
import type { AdDetails } from '@amzn/safeframe-model';
import { ClientReporter } from './clientReporter.js';
import { ClientMessageListeners } from './clientMessageReceiver.js';
import type { ClientApis } from './types/types.js';
import type { ComputedAdDetailsData, PixelOptionsData } from '../components/commands.js';
import type { CommonSupportedCommands } from '../host/api-client/CommonSupportedCommands.js';
import { sendCriticalFeatureAndLoaded } from './commonSf.js';
import { GeomMessage, SafeFrameExtMessage } from '@/host/api-client/handlers/sendSafeFrameExt.js';
import { geomCache, inViewPercentageCache } from '@/client/apiPage/sfApi.js';
import { DEDUPE_SF_VIDEO_PIXELS } from '@/components/weblabs.js';
import { CollapseCode } from '@/host/domain/placement.js';

declare global {
    interface Window {
        aPageStart?: number | undefined;
        adStartTime?: number | undefined;
    }
}

export abstract class CommonApiImplementation implements SFClientVideoAPI, SFClientAPI, InternalSFClientAPI {
    public readonly cr: ClientReporter;

    constructor(
        protected readonly cms: ClientMessageSender,
        protected readonly o: AdDetails,
        public readonly mL: ClientMessageListeners,
    ) {
        this.cr = new ClientReporter(this as unknown as ClientApis, o);
    }

    fireImpression = async (_data: PixelOptionsData) => {
        this.sendLatencyMetric(LOADED_HOST_SCOPE);
        await this.cr.fireCodImpressionPixel();
    };

    changeSize = (width: string, height: string) => {
        this.cms.sendMessage<CommonSupportedCommands['changeSize']>('changeSize', { width: width, height: height });
    };
    logCsaEvent = (metricName: string) => {
        this.cms.sendMessage<CommonSupportedCommands['logCsaEvent']>('logCsaEvent', { metricName: metricName });
    };
    registerCustomMessageListener = (msgKey: string, callback: any) => {
        this.mL[msgKey] = callback;
        this.countMetric('sf:commonmain:registerCustomMessageListener', 1, true);
    };
    sendLatencyMetric = (latencyMetricType: LatencyMetricType) => {
        this.cms.sendMessage<CommonSupportedCommands['sendLatencyMetric']>('sendLatencyMetric', {
            latencyMetricType: latencyMetricType,
            timestamp: new Date(),
        });
    };
    countMetric = (msg: string, num: number = 1, isGlobal: boolean = false) => {
        this.cms.sendMessage<CommonSupportedCommands['countMetric']>('countMetric', {
            metricMsg: msg,
            value: num,
            isGlobal: isGlobal,
        });
    };
    fireViewableLatencyMetrics = () => {
        this.cms.sendMessage<CommonSupportedCommands['fireViewableLatencyMetrics']>('fireViewableLatencyMetrics', {
            adLoadedTimestamp: Date.now(),
        });
    };
    /**
     * @desc Used by Creative template owners to trigger metric for 'Sponsored Label' rendered.
     */
    feedbackLabelRendered = () => {
        this.cms.sendMessage<CommonSupportedCommands['feedbackLabelRendered']>('feedbackLabelRendered');
    };
    /**
     * @desc Used by Creative template owners to open feedback Modal.
     */
    feedbackOpenModal = () => {
        this.cms.sendMessage<CommonSupportedCommands['feedbackOpenModal']>('feedbackOpenModal');
    };

    updateViewability = (data: ExtendedUpdateViewabilityData) => {
        const clientSfViewableInfo = produceViewableInfo();
        // update cached viewability for synchronous viewability access
        update(data);
        if (!this.o.aaxInstrPixelUrl || !clientSfViewableInfo.payload || !clientSfViewableInfo.viewabilityStandards) {
            return;
        }
        const geom = window?.$sf?.ext?.geom && window?.$sf?.ext.geom();
        const cachedViewability = geom?.self?.iv ? geom?.self?.iv : null;
        if (isVideo(this.o.mediaType)) {
            const clientSfPlayingTimeInfo = producePlayingTimeInfo();
            this.cr.fireViewablePixelsForVideo(
                cachedViewability,
                clientSfViewableInfo.payload(),
                clientSfViewableInfo.viewabilityStandards(),
                this.o.isNoInventory ? true : false,
                clientSfPlayingTimeInfo.playingTime ? clientSfPlayingTimeInfo.playingTime() : null,
            );
        }
    };

    /**
     * Viewability pixels for video are fired from a combination of time spent in the view port and time spent playing.
     * For ad templates which implement their own video player (ie Sponsored Display's PERC), they need to tell SF how much
     * time has been spent playing. SF will combine this with its knowledge of in view time to determine when to fire a
     * viewability pixel.
     *
     * @param continuousPlaybackSeconds - This is the maximum amount of continuous playback time the videoplayer has observed (in seconds)
     */
    updatePlayingTimeForVideo = (continuousPlaybackSeconds: number) => {
        if (DEDUPE_SF_VIDEO_PIXELS().isT1()) {
            // Send message from iframe to host on the updated continuousPlaybackSeconds
            this.cms.sendMessage<CommonSupportedCommands['sendSfVideoContinuousPlaytimeSeconds']>(
                'sendSfVideoContinuousPlaytimeSeconds',
                {
                    sfVideoContinuousPlaytimeSeconds: continuousPlaybackSeconds,
                },
            );
        } else {
            const clientSfViewableInfo = produceViewableInfo();
            updatePlayingTime(continuousPlaybackSeconds);
            const geom = window?.$sf?.ext?.geom && window.$sf.ext.geom();
            const cachedViewability = geom?.self?.iv;
            if (cachedViewability && clientSfViewableInfo.payload && clientSfViewableInfo.viewabilityStandards) {
                const clientSfPlayingTimeInfo = producePlayingTimeInfo();
                this.cr.fireViewablePixelsForVideo(
                    cachedViewability,
                    clientSfViewableInfo.payload(),
                    clientSfViewableInfo.viewabilityStandards(),
                    this.o.isNoInventory ? true : false,
                    clientSfPlayingTimeInfo.playingTime ? clientSfPlayingTimeInfo.playingTime() : null,
                );
            }
        }
    };
    requestVideoAutoplay = () => {
        this.cms.sendMessage<CommonSupportedCommands['requestVideoAutoplay']>('requestVideoAutoplay');
    };
    releaseVideoAutoplay = () => {
        this.cms.sendMessage<CommonSupportedCommands['releaseVideoAutoplay']>('releaseVideoAutoplay');
    };
    haveVideoAutoplayPermission = () => {
        this.cms.sendMessage<CommonSupportedCommands['haveVideoAutoplayPermission']>('haveVideoAutoplayPermission');
    };

    isOnAmazon = (): boolean => {
        return true;
    };
    logError = (message: string, error?: Error, level?: number) => {
        this.cms.sendMessage<CommonSupportedCommands['logError']>('logError', { message, error, level });
    };
    logFatal = (message: string, error?: Error, level?: number) => {
        this.cms.sendMessage<CommonSupportedCommands['logFatal']>('logFatal', { message, error, level });
    };

    /**
     * @deprecated Use collapseSlot instead
     */
    collapseSlotInternal = (code: CollapseCode) => {
        this.logAPIInvocation('collapseSlot');
        this.cms.sendMessage<CommonSupportedCommands['collapseSlot']>('collapseSlot', code);
    };
    collapseSlot = () => {
        this.collapseSlotInternal('CREATIVE');
    };

    resizeCreativeWrapper = (data: { height: string; width: string }) => {
        const creativeWrapper = window.document.getElementById(`ape_${this.o.scope}_creativeWrapper`);
        if (creativeWrapper === null) {
            return;
        }

        creativeWrapper.style.height = data.height;
        creativeWrapper.style.width = data.width;
    };

    /**
     * Used to emit a custom event to the ad notifying that an event occurred outside the safeframe.
     * @param data object containing the name of the event which is being propagated to the ad
     */
    triggerEventInIframe = (data: { eventType: string }) => {
        window.dispatchEvent(new Event('outerpage' + data.eventType));
    };

    logAPIInvocation: LogInvocation = (apiName: string, ...apiParams: any[]) => {
        const API = 'messenger_';
        this.cms.sendMessage<CommonSupportedCommands['logAPIInvocation']>('logAPIInvocation', {
            apiName: apiName,
            apiParams: apiParams,
        });
        this.cms.sendMessage<CommonSupportedCommands['logCsaEvent']>('logCsaEvent', { metricName: API + apiName });
    };

    setupComputedAdDetails = (data: ComputedAdDetailsData) => {
        window.aPageStart = data.aPageStart;
        window.adStartTime = data.adStartTime;
    };

    signalAdLoadedAndInteractive = () => {
        sendCriticalFeatureAndLoaded(this);
    };

    /**
     *
     * Client sends domain event to host to fire the
     * COD impression pixel using the Light Ads logic
     */
    sendEventAddedCreative = () => {
        this.cms.sendMessage<CommonSupportedCommands['sendEventAddedCreative']>('sendEventAddedCreative');
    };

    populateGeom = (message: GeomMessage): void => geomCache.set(message.geom);

    populateSafeFrameExt = (message: SafeFrameExtMessage): void => {
        inViewPercentageCache.set(message.inViewPercentage);
        geomCache.set(message.geom);
    };
}
